import {
    EmptyBox,
    HeaderListSkeleton,
    LazyBreadcrumb,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import {
    FactureOpenSearch,
    FactureOpenSearchSelectorMain,
    FACTURES_OPEN_SEARCH_SLICE_NAME,
    useFacturationService,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FacturesTable,
    PossibleTypes,
    useDefaultContextActions,
    useSearchBar,
} from '@europrocurement/l2d-modules';
import { AnyAction } from '@reduxjs/toolkit';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { Box } from '@mui/material';
import { useFormalitiesInvoicesBackHook } from '../../hooks';

export const InvoicesFormalitiesListBack: React.FunctionComponent = function () {
    const dispatch = useDispatch();
    const [isReady, setIsReady] = useState(false);
    const datasource: DataSource<FactureOpenSearch> = useSelector(FactureOpenSearchSelectorMain);
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { backContextActions } = useDefaultContextActions();
    useDumbActionsContext(backContextActions);

    /** Formality Domain 3 */
    const ddmIdFilter: number = 3;

    const { columns, filters, rowActions, preFilters } = useFormalitiesInvoicesBackHook({
        ddmIdFilter,
    });
    const { getFactureOpenSearch } = useFacturationService();

    // Activation du SearchBar pour les factures de formalités
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityInvoice);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    const breadcrumbItems = [
        {
            link: '/accueil',
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: { display: 'flex', alignItems: 'center' },
            },
        },
        {
            link: '/formalites/factures',
            path: 'Formalités',
        },
        'Liste des factures de formalité',
    ];

    useEffect(() => {
        dispatch({
            type: `${FACTURES_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'ddmId',
                value: ddmIdFilter,
            },
        });
        setIsReady(true);
    }, [ddmIdFilter, dispatch]);

    // reFetch factures api datas
    useEffect(() => {
        dispatch(getFactureOpenSearch({}) as unknown as AnyAction);
    }, [dispatch, getFactureOpenSearch]);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des factures."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <LazyBreadcrumb
                path={breadcrumbItems}
                separator=" / "
                sx={{ marginBottom: '1.5em' }}
            />
            {isReady && (
                <FacturesTable
                    datasource={datasource}
                    preFilters={preFilters}
                    columns={columns}
                    filters={filters}
                    fetchData={getFactureOpenSearch}
                    rowActions={rowActions}
                    onRowClick={() => {}}
                />
            )}
        </SkeletonLoader>
    );
};
